/**
 * Patches browser console so non-error methods respect the client log level gate.
 * console.error always remains available. Enable more via:
 *   NEXT_PUBLIC_CLIENT_LOG_LEVEL=warn|info|debug
 *   NEXT_PUBLIC_CLIENT_LOG_VERBOSE=true
 *
 * Even when verbose, arguments that look like JWTs / bearer tokens are redacted.
 */

import { logger, type ClientLogLevel } from '@/lib/logger'

declare global {
  interface Window {
    __CYBERWELL_CONSOLE_GATE__?: boolean
  }
}

const LEVEL_ORDER: Record<ClientLogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
}

const JWT_LIKE = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/
const BEARER_LIKE = /Bearer\s+[A-Za-z0-9._~+/=-]+/i
const SENSITIVE_HEADER_KEY = /^(authorization|x-xsrf-token|x-csrf-token|x-session-id)$/i

function resolveConfiguredLevel(): ClientLogLevel {
  if (typeof process === 'undefined') return 'error'
  const verbose = process.env.NEXT_PUBLIC_CLIENT_LOG_VERBOSE?.trim().toLowerCase()
  if (verbose === '1' || verbose === 'true' || verbose === 'yes') {
    return 'debug'
  }
  const raw = process.env.NEXT_PUBLIC_CLIENT_LOG_LEVEL?.trim().toLowerCase()
  if (raw === 'warn' || raw === 'info' || raw === 'debug' || raw === 'error') {
    return raw
  }
  return 'error'
}

function shouldEmit(level: ClientLogLevel): boolean {
  return LEVEL_ORDER[level] <= LEVEL_ORDER[resolveConfiguredLevel()]
}

function redactString(value: string): string {
  return value
    .replace(BEARER_LIKE, 'Bearer [REDACTED]')
    .replace(JWT_LIKE, '[REDACTED_JWT]')
}

function redactArg(arg: unknown): unknown {
  if (typeof arg === 'string') {
    return redactString(arg)
  }
  if (arg && typeof arg === 'object' && !Array.isArray(arg)) {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(arg as Record<string, unknown>)) {
      if (SENSITIVE_HEADER_KEY.test(key)) {
        out[key] = '[REDACTED]'
      } else if (typeof value === 'string') {
        out[key] = redactString(value)
      } else {
        out[key] = value
      }
    }
    return out
  }
  return arg
}

function redactArgs(args: unknown[]): unknown[] {
  return args.map(redactArg)
}

export function installClientConsoleGate(): void {
  if (typeof window === 'undefined') return
  if (window.__CYBERWELL_CONSOLE_GATE__) return
  window.__CYBERWELL_CONSOLE_GATE__ = true

  const original = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  }

  console.log = (...args: unknown[]) => {
    if (shouldEmit('info')) original.log(...redactArgs(args))
  }
  console.info = (...args: unknown[]) => {
    if (shouldEmit('info')) original.info(...redactArgs(args))
  }
  console.debug = (...args: unknown[]) => {
    if (shouldEmit('debug')) original.debug(...redactArgs(args))
  }
  console.warn = (...args: unknown[]) => {
    if (shouldEmit('warn')) original.warn(...redactArgs(args))
  }
  // Errors always emit, but still redact token material
  console.error = (...args: unknown[]) => {
    original.error(...redactArgs(args))
  }

  // Prefer logger for new code; gate already covers legacy console.* call sites
  void logger
}
